/**
 * Same-board duplicate persons (2026-10-07) — two person records for one human
 * on the same board. The dry run proposes one survivor per group: the record
 * with the earliest start a filing states (then Cynapse's earliest tenure, more
 * tenures, the older record). Apply merges only the groups the operator ticks,
 * through the same Assayer-first person merge as the identity review page.
 */
import { apiClient } from './config.js';
import { waitForMaintenanceJob, type MaintenanceJobStatus } from './tools.js';

export interface SameBoardPerson { entity_id: string; name: string | null; first_seen: string | null }

export interface SameBoardDrop {
  loser_id: string; company_entity_id: string; role: string;
  loser_valid_from: string | null; survivor_valid_from: string | null; earlier_start_lost: boolean;
}

export interface SameBoardProposal {
  group_key: string;
  codes: string[];
  companies: { entity_id: string; name: string | null }[];
  survivor: SameBoardPerson;
  losers: SameBoardPerson[];
  reason: string;
  dropped_tenures: SameBoardDrop[];
  earlier_start_lost: boolean;
}

export interface SameBoardDryRun {
  dry_run: true; groups: number; persons_to_merge: number;
  groups_losing_an_earlier_start: number; proposals: SameBoardProposal[];
}

export interface SameBoardApplyResult {
  dry_run: false; groups: number; merged: number;
  results: { survivor_id: string; loser_ids: string[]; merged: boolean; error?: string; bindings_rebound?: number }[];
}

async function enqueue(dryRun: boolean, approved: { survivor_id: string; loser_ids: string[] }[]) {
  const { data } = await apiClient.post<{ job_id: string; note?: string }>(
    '/tools/same-board-person-merge', { approved }, { params: { dry_run: dryRun } },
  );
  if (data.note) throw new Error('A same-board merge job is already running — try again when it finishes.');
  return data.job_id;
}

export async function proposeSameBoardMerges(onProgress?: (s: MaintenanceJobStatus) => void): Promise<SameBoardDryRun> {
  return waitForMaintenanceJob<SameBoardDryRun>(await enqueue(true, []), onProgress, 2000);
}

export async function applySameBoardMerges(
  approved: { survivor_id: string; loser_ids: string[] }[],
  onProgress?: (s: MaintenanceJobStatus) => void,
): Promise<SameBoardApplyResult> {
  return waitForMaintenanceJob<SameBoardApplyResult>(await enqueue(false, approved), onProgress, 2000);
}
